import * as React from "react"
import { Layer } from "../../render/presentation/Layer"
import { EmptyState } from "../EmptyState"
import { ConstraintValues, getMergedConstraintsProps } from "../../render/types/Constraints"
import { Rect } from "../../render/types/Rect"
import { Size } from "../../render/types/Size"
import { WithFractionOfFreeSpace } from "../../render/traits/FreeSpace"
import { RenderTarget } from "../../render/types/RenderEnvironment"
import { Point, PropertyControls, ControlType, Line } from "../../render"
import { BackgroundImage } from "../../render/types/BackgroundImage"
import * as StackLayout from "./layoutUtils"
import {
    DeprecatedStackProperties,
    StackState,
    StackSpecificProps,
    StackAlignment,
    StackPlaceHolders,
    StackDistribution,
    StackDirection,
} from "./types"
import { isReactChild, isReactElement } from "../../utils/type-guards"
import {
    DeprecatedFrame as DeprecatedCoreFrame,
    DeprecatedFrameWithEventsProps as DeprecatedFrameProps,
    DeprecatedFrameWithEvents as DeprecatedFrame,
} from "../../render/presentation/Frame"
import { paddingFromProps } from "../utils/paddingFromProps"
import { ConsumeParentSize, deprecatedParentSize } from "../../render/types/NewConstraints"

/**
 * @beta
 */
const stackAlignmentOptions: StackAlignment[] = ["start", "center", "end"]
/**
 * @beta
 */
const genericAlignmentTitles = stackAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Start"
        case "center":
            return "Center"
        case "end":
            return "End"
    }
})
/**
 * @beta
 */
const horizontalAlignmentTitles = stackAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Left"
        case "center":
            return "Center"
        case "end":
            return "Right"
    }
})
/**
 * @beta
 */
const verticalAlignmentTitles = stackAlignmentOptions.map(option => {
    switch (option) {
        case "start":
            return "Top"
        case "center":
            return "Center"
        case "end":
            return "Bottom"
    }
})

/**
 * The Stack component will automatically distribute it's contents based on its
 * props. See `StackProperties` for details on configuration.
 *
 * @remarks
 * ```jsx
 * const MyComponent: React.SFC<StackProperties> = props => {
 *   return (
 *     <Stack {...props}>
 *       <Frame>Hello</Frame>
 *     </Stack>
 *   )
 * }
 * ```
 * @internal
 */
export class DeprecatedStack extends Layer<Partial<DeprecatedStackProperties>, StackState> {
    /** @internal */
    static userInterfaceName = "Stack"

    /** @internal */
    static _isStylable = true

    /** @internal */
    static defaultStackSpecificProps: StackSpecificProps = {
        direction: "vertical",
        distribution: "space-around",
        alignment: "center",
        gap: 10,
        padding: 0,
        paddingPerSide: false,
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
    }

    /** @internal */
    static readonly defaultProps: DeprecatedStackProperties = {
        ...Layer.defaultProps,
        ...DeprecatedCoreFrame.defaultFrameSpecificProps,
        ...DeprecatedStack.defaultStackSpecificProps,
        background: null,
    }

    /** @internal */
    props: DeprecatedStackProperties

    /** @internal */
    static propertyControls: PropertyControls<DeprecatedStackProperties> = {
        direction: {
            type: ControlType.SegmentedEnum,
            options: ["horizontal", "vertical"],
            title: "Direction",
            defaultValue: "vertical",
        },
        distribution: {
            type: ControlType.Enum,
            options: ["start", "center", "end", "space-between", "space-around", "space-evenly"],
            optionTitles: ["Start", "Center", "End", "Space Between", "Space Around", "Space Evenly"],
            title: "Distribute",
            defaultValue: "space-around",
        },
        alignment: {
            type: ControlType.SegmentedEnum,
            options: ["start", "center", "end"],
            optionTitles(props) {
                if (!props) return genericAlignmentTitles
                const crossDirectionIsHorizontal = props.direction !== "horizontal"
                return crossDirectionIsHorizontal ? horizontalAlignmentTitles : verticalAlignmentTitles
            },
            title: "Align",
            defaultValue: "center",
        },
        gap: {
            type: ControlType.Number,
            min: 0,
            title: "Gap",
            hidden: props => {
                return (
                    props.distribution !== undefined &&
                    ["space-between", "space-around", "space-evenly"].indexOf(props.distribution) !== -1
                )
            },
            defaultValue: 10,
        },
        padding: {
            type: ControlType.FusedNumber,
            toggleKey: "paddingPerSide",
            toggleTitles: ["Padding", "Padding per side"],
            valueKeys: ["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"],
            valueLabels: ["t", "r", "b", "l"],
            min: 0,
            title: "Padding",
            defaultValue: 0,
        },
    }

    /** @internal */
    get rect() {
        return StackLayout.calcStackRect(this.props, null)
    }

    /** @internal */
    state: StackState = {
        size: null,
        shouldCheckImageAvailability: true,
        currentBackgroundImageSrc: null,
    }

    /** @internal */
    static getDerivedStateFromProps(
        nextProps: Partial<DeprecatedStackProperties>,
        prevState: StackState
    ): StackState | null {
        const size = StackLayout.calcUpdatedStackComponentSize(nextProps, prevState)
        const target = RenderTarget.current()
        const nextBackgroundImageSrc =
            nextProps.background && BackgroundImage.isImageObject(nextProps.background)
                ? nextProps.background.src
                : null
        if (nextBackgroundImageSrc) {
            const shouldCheckImageAvailability = prevState.currentBackgroundImageSrc !== nextBackgroundImageSrc
            if (shouldCheckImageAvailability !== prevState.shouldCheckImageAvailability) {
                return {
                    size: size,
                    currentBackgroundImageSrc: nextBackgroundImageSrc,
                    shouldCheckImageAvailability: shouldCheckImageAvailability,
                }
            }
        }
        if (prevState.size) {
            if (target === RenderTarget.preview) {
                return null
            }
            if (prevState.size.width === size.width && prevState.size.height === size.height) {
                return null
            }
        }
        return {
            size: size,
            currentBackgroundImageSrc: nextBackgroundImageSrc,
            shouldCheckImageAvailability: prevState.shouldCheckImageAvailability,
        }
    }

    /** @internal */
    render() {
        return (
            <ConsumeParentSize>
                {({ size: newParentSize }) => {
                    const parentSize = deprecatedParentSize(newParentSize)

                    const props = this.props
                    const { visible, placeholders, _forwardedOverrides } = props
                    if (!visible) {
                        return null
                    }

                    let children = this.props.children

                    if (_forwardedOverrides && children) {
                        children = React.Children.map(this.props.children, child => {
                            return React.cloneElement(child as any, { _forwardedOverrides })
                        })
                    }

                    // Layout

                    const invisibleItems = StackLayout.invisibleItemIndexes(children, placeholders)

                    const padding = StackLayout.calcPaddingSize(props)

                    const constraintValues = ConstraintValues.fromProperties(props)
                    const minSize = ConstraintValues.toMinSize(constraintValues, parentSize)
                    const minContentSize = Size.subtract(minSize, padding)

                    const minChildrenSizes = StackLayout.calcMinChildSizes(children, minContentSize, placeholders)
                    const autoSize = DeprecatedStack.autoSize(minChildrenSizes, props, invisibleItems)
                    const size = ConstraintValues.toSize(constraintValues, parentSize, autoSize, null)

                    const childFractions = StackLayout.calcChildFractions(children)
                    const freeSpaceForChildren = DeprecatedStack.freeSpace(size, autoSize, childFractions, props)

                    const contentSize = Size.subtract(size, padding)
                    const childSizes = StackLayout.calcChildSizes(
                        children,
                        contentSize,
                        freeSpaceForChildren,
                        placeholders
                    )
                    const childRects = StackLayout.calcChildLayoutRects(childSizes, contentSize, props, invisibleItems)

                    const frameProps: Partial<DeprecatedFrameProps> = Object.assign({}, this.props)
                    Object.keys(DeprecatedStack.defaultStackSpecificProps).map(key => {
                        delete frameProps[key]
                    })
                    const borderProps = this.props as any
                    if (borderProps._border) {
                        const { borderWidth, borderColor, borderStyle } = borderProps._border
                        Object.assign(frameProps, { borderWidth, borderColor, borderStyle })
                    }

                    return (
                        <DeprecatedFrame {...frameProps}>
                            {this.layoutChildren(childRects, placeholders)}
                            {this.layoutPlaceholders(childRects, placeholders)}
                            <EmptyState
                                title={"Drop items"}
                                children={this.props.children!}
                                size={size}
                                showArrow={false}
                                hide={!!this.props.placeholders}
                                insideUserCodeComponent={!props.__fromCodeComponentNode}
                            />
                        </DeprecatedFrame>
                    )
                }}
            </ConsumeParentSize>
        )
    }

    /** @internal */
    static _cssExport(props: StackSpecificProps): React.CSSProperties {
        const style: React.CSSProperties = {
            display: "flex",
            flexDirection: props.direction === "horizontal" ? "row" : "column",
            justifyContent: stackDistributionAndAlignmentToFlexbox(props.distribution),
            alignItems: stackDistributionAndAlignmentToFlexbox(props.alignment),
        }

        const { top, left, right, bottom } = paddingFromProps(props)
        style.padding = `${top}px ${right}px ${bottom}px ${left}px`

        return style
    }

    /** @internal */
    static paddingSize(props: DeprecatedStackProperties) {
        return StackLayout.calcPaddingSize(props)
    }

    /** @internal */
    static childLayoutRects(
        childSizes: Size[],
        size: Size,
        props: DeprecatedStackProperties,
        invisibleItems: number[]
    ): Rect[] {
        return StackLayout.calcChildLayoutRects(childSizes, size, props, invisibleItems)
    }

    /** @internal */
    static autoSize(minChildrenSizes: Size[], props: DeprecatedStackProperties, invisibleItems: number[]) {
        return StackLayout.calcAutoSize(minChildrenSizes, props, invisibleItems)
    }

    /** @internal */
    static freeSpace(
        size: Size,
        autoSize: Size,
        childFractions: Size,
        props: DeprecatedStackProperties
    ): WithFractionOfFreeSpace {
        return StackLayout.calcFreeSpace(size, autoSize, childFractions, props)
    }

    private layoutChildren(childRects: Rect[], placeholders?: StackPlaceHolders) {
        type Props = {
            top: number
            left: number
            width: number
            height: number
            right: null
            bottom: null
            style: any
        }

        return React.Children.map(this.props.children, (child: React.ReactElement<Props>, index) => {
            if (!isReactChild(child) || !isReactElement(child)) {
                return child
            }

            if (placeholders && index >= placeholders.index) {
                index += placeholders.sizes.length
            }

            const rect = childRects[index]
            const constraints = {
                top: rect.y,
                left: rect.x,
                width: rect.width,
                height: rect.height,
                right: null,
                bottom: null,
                _constraints: { enabled: true },
            }

            const constraintProps = getMergedConstraintsProps(child.props, constraints)

            const props: any = {
                ...constraintProps,
                style: placeholders
                    ? {
                          ...child.props.style,
                          WebkitTransition: "transform 0.2s ease-out",
                      }
                    : child.props.style,
            }

            return React.cloneElement<Props>(child, props)
        })
    }

    private layoutPlaceholders(childRects: Rect[], placeholders?: StackPlaceHolders) {
        if (!placeholders) return null
        return placeholders.sizes.map((size, index) => {
            const rect = childRects[placeholders.index + index]
            return (
                <DeprecatedFrame
                    key={`stack-placeholder-${index}`}
                    top={rect.y}
                    left={rect.x}
                    width={rect.width}
                    height={rect.height}
                    style={{ WebkitTransition: "transform 0.2s ease-out" }}
                />
            )
        })
    }

    /**
     * @alpha
     */
    static childInsertion(
        childRects: Rect[],
        containerSize: Size,
        insertion: Point,
        insertionMaxSize: Size,
        properties: DeprecatedStackProperties
    ): { index: number; line: Line } {
        let closestDistance = Infinity
        let closestChildIndex = null

        const isVertical = properties.direction === "vertical"

        childRects.forEach((childRect, i) => {
            const distance = isVertical ? Math.abs(insertion.y - childRect.y) : Math.abs(insertion.x - childRect.x)
            if (distance > closestDistance) return
            closestDistance = distance
            closestChildIndex = i
        })

        let index = 0
        if (closestChildIndex !== null) {
            const closestCenter = Rect.center(childRects[closestChildIndex])
            const after = isVertical ? insertion.y > closestCenter.y : insertion.x > closestCenter.x
            if (after) closestChildIndex++

            index = Math.min(Math.max(0, closestChildIndex), childRects.length)
        }

        const crossPaddingOffset = crossPadding(properties)
        const line = insertionIndicatorLine(
            properties.direction,
            properties.alignment,
            crossPaddingOffset,
            containerSize,
            childRects,
            index,
            insertionMaxSize
        )
        return { index, line }
    }
}

function crossPadding(props: DeprecatedStackProperties) {
    const { direction, alignment, paddingPerSide, padding } = props

    if (alignment === "center") return 0
    if (!paddingPerSide) return padding

    const alignStart = alignment === "start"
    if (direction === "vertical") {
        return alignStart ? props.paddingLeft : props.paddingRight
    } else {
        return alignStart ? props.paddingTop : props.paddingBottom
    }
}

// Insertion line

function insertionIndicatorLine(
    direction: StackDirection,
    crossAlignment: StackAlignment,
    crossPaddingOffset: number,
    containerSize: Size,
    childrenRects: Rect[],
    index: number,
    insertedLayerSize: Size
): Line {
    const beforeRect: Rect | undefined = childrenRects[index - 1]
    const afterRect: Rect | undefined = childrenRects[index]
    const isVertical = direction === "vertical"

    const c = { x: containerSize.width / 2, y: containerSize.height / 2 }

    if (!beforeRect && !afterRect) {
    } else if (childrenRects.length === 1) {
        const onlyRect = beforeRect || afterRect
        if (index === 0) {
            isVertical ? (c.y = onlyRect.y - 10) : (c.x = onlyRect.x - 10)
        } else {
            isVertical ? (c.y = onlyRect.y + onlyRect.height + 10) : (c.x = onlyRect.x + onlyRect.width + 10)
        }
    } else if (beforeRect && afterRect) {
        const delta = Math.abs(
            isVertical ? beforeRect.y + beforeRect.height - afterRect.y : beforeRect.x + beforeRect.width - afterRect.x
        )
        isVertical ? (c.y = afterRect.y - delta / 2) : (c.x = afterRect.x - delta / 2)
    } else if (beforeRect) {
        const beforeBefore = childrenRects[index - 2]
        const delta = Math.abs(
            isVertical
                ? beforeBefore.y + beforeBefore.height - beforeRect.y
                : beforeBefore.x + beforeBefore.width - beforeRect.x
        )
        isVertical
            ? (c.y = beforeRect.y + beforeRect.height + delta / 2)
            : (c.x = beforeRect.x + beforeRect.width + delta / 2)
    } else if (afterRect) {
        const afterAfter = childrenRects[index + 1]
        const delta = Math.abs(
            isVertical ? afterRect.y + afterRect.height - afterAfter.y : afterRect.x + afterRect.width - afterAfter.x
        )
        isVertical ? (c.y = afterRect.y - delta / 2) : (c.x = afterRect.x - delta / 2)
    }
    c.x = Math.min(Math.max(4, c.x), containerSize.width - 8)
    c.y = Math.min(Math.max(4, c.y), containerSize.height - 8)
    return createLine(direction, crossAlignment, crossPaddingOffset, c, containerSize, insertedLayerSize)
}

function createLine(
    direction: StackDirection,
    crossAlignment: StackAlignment,
    crossPaddingOffset: number,
    centerPoint: Point,
    containerSize: Size,
    insertedLayerSize: Size
): Line {
    const isVertical = direction === "vertical"
    let a: Point
    let b: Point
    const crossOffset = 10 + crossPaddingOffset

    switch (crossAlignment) {
        case "center":
            a = {
                x: isVertical ? centerPoint.x - insertedLayerSize.width / 2 : centerPoint.x,
                y: isVertical ? centerPoint.y : centerPoint.y - insertedLayerSize.height / 2,
            }
            b = {
                x: isVertical ? centerPoint.x + insertedLayerSize.width / 2 : centerPoint.x,
                y: isVertical ? centerPoint.y : centerPoint.y + insertedLayerSize.height / 2,
            }
            break
        case "start":
            a = {
                x: isVertical ? crossOffset : centerPoint.x,
                y: isVertical ? centerPoint.y : crossOffset,
            }
            b = {
                x: isVertical ? insertedLayerSize.width + crossOffset : centerPoint.x,
                y: isVertical ? centerPoint.y : insertedLayerSize.height + crossOffset,
            }
            break
        case "end":
        default:
            a = {
                x: isVertical ? containerSize.width - insertedLayerSize.width - crossOffset : centerPoint.x,
                y: isVertical ? centerPoint.y : containerSize.height - insertedLayerSize.height - crossOffset,
            }
            b = {
                x: isVertical ? containerSize.width - crossOffset : centerPoint.x,
                y: isVertical ? centerPoint.y : containerSize.height - crossOffset,
            }
            break
    }
    return { a, b }
}

function stackDistributionAndAlignmentToFlexbox(alignment: StackDistribution | StackAlignment) {
    switch (alignment) {
        case "start":
            return "flex-start"
        case "end":
            return "flex-end"
        default:
            return alignment
    }
}
